/**
 * FMComponent Registry — autocomplete data for the <fm:*> template tag system.
 *
 * Component tag syntax:  <fm:ComponentName attribute="value">...</fm:ComponentName>
 * Variable syntax:       {$$variableName}  or  {$variableName}
 *
 * This registry is derived from the PHP FMComponent classes at
 * platform/lib/FMComponent/ and their getAttributeList() / getTemplateVariables() methods.
 */

export interface FMComponentAttribute {
	name: string;
	description: string;
	type?: string;
	required?: boolean;
	default?: string;
	values?: string[]; // enumerated allowed values
}

export interface FMComponentVariable {
	name: string;
	description: string;
}

export interface FMComponentDef {
	name: string;
	description: string;
	category: string;
	selfClosing?: boolean;
	attributes: FMComponentAttribute[];
	variables?: FMComponentVariable[];
	snippet?: string; // default snippet when inserted
}

// ─── Category helpers ────────────────────────────────────────────────
const cat = {
	media: 'Media',
	user: 'Users',
	layout: 'Layout & Logic',
	form: 'Form',
	nav: 'Navigation',
	social: 'Social',
	util: 'Utility',
	widget: 'Widget',
};

// ─── Component definitions ───────────────────────────────────────────
export const fmComponents: FMComponentDef[] = [
	// ═══ MEDIA ═══
	{
		name: 'MediaList2',
		description: 'Loop through a filtered list of media items with pagination support',
		category: cat.media,
		attributes: [
			{ name: 'channel', description: 'Filter by channel shortname or id', type: 'string' },
			{ name: 'collection', description: 'Collection ID for filtering', type: 'integer' },
			{ name: 'dateFormat', description: 'PHP date() format string', type: 'string', default: '%Y-%m-%d %H:%i:%s' },
			{ name: 'fields', description: 'Comma-separated list of fields to retrieve', type: 'string' },
			{ name: 'fileTypes', description: 'Filter by file type (1=image, 2=video, 3=audio, 4=text)', type: 'string' },
			{ name: 'groupId', description: 'Filter by group ID', type: 'integer' },
			{ name: 'eventId', description: 'Filter by event ID', type: 'integer' },
			{ name: 'pageSize', description: 'Items per page, max 200', type: 'integer', default: '30' },
			{ name: 'offset', description: 'Start position in results', type: 'integer' },
			{ name: 'sort', description: 'Sort criteria with direction', type: 'string', default: 'upload DESC' },
			{ name: 'searchQuery', description: 'Full-text search query', type: 'string' },
			{ name: 'tag', description: 'Filter by tag', type: 'string' },
			{ name: 'uid', description: 'Filter by user ID', type: 'integer' },
			{ name: 'moderationStatus', description: 'Moderation filter', type: 'string', values: ['unmoderated', 'accepted', 'denied', 'notdenied', 'moderated', 'all'] },
			{ name: 'startTime', description: 'Date range start', type: 'string' },
			{ name: 'endTime', description: 'Date range end', type: 'string' },
			{ name: 'context', description: 'Media type context', type: 'string', values: ['generic', 'all', 'comment', 'note', 'parent', 'child'] },
			{ name: 'geoBoundaries', description: 'Geo filter as {west},{north},{east},{south}', type: 'string' },
		],
		variables: [
			{ name: '$media.id', description: 'Media ID' },
			{ name: '$media.title', description: 'Title' },
			{ name: '$media.message', description: 'Description/message' },
			{ name: '$media.duration', description: 'Duration (video/audio)' },
			{ name: '$media.tags', description: 'Comma-separated tags' },
			{ name: '$media.rating', description: 'Average rating' },
			{ name: '$media.upload', description: 'Upload date' },
			{ name: '$media.filetype', description: 'File type (1=image, 2=video, etc.)' },
			{ name: '$media.user_name', description: 'Uploader name' },
			{ name: '$media.user_city', description: 'Uploader city' },
			{ name: '$media.thumbUrl', description: 'Thumbnail URL' },
			{ name: '$media.publicUrl', description: 'Public media URL' },
			{ name: '$media.views', description: 'View count' },
			{ name: '$media.votes', description: 'Vote count' },
			{ name: '$media.comments', description: 'Comment count' },
			{ name: '$pager.currentPage', description: 'Current page number' },
			{ name: '$pager.totalPages', description: 'Total pages' },
			{ name: '$pager.totalItems', description: 'Total item count' },
		],
		snippet: '<fm:MediaList2 pageSize="20" sort="upload DESC">\n\t{$$media.title}\n</fm:MediaList2>',
	},
	{
		name: 'MediaList',
		description: 'Simple media list (legacy, prefer MediaList2)',
		category: cat.media,
		attributes: [
			{ name: 'channel', description: 'Filter by channel', type: 'string' },
			{ name: 'pageSize', description: 'Items per page', type: 'integer', default: '30' },
			{ name: 'sort', description: 'Sort order', type: 'string' },
			{ name: 'fileTypes', description: 'Filter by file type', type: 'string' },
			{ name: 'uid', description: 'Filter by user ID', type: 'integer' },
			{ name: 'moderationStatus', description: 'Moderation filter', type: 'string' },
		],
		variables: [
			{ name: '$media.id', description: 'Media ID' },
			{ name: '$media.title', description: 'Title' },
			{ name: '$media.upload', description: 'Upload date' },
		],
	},
	{
		name: 'VideoPlayer',
		description: 'Embed a video player',
		category: cat.media,
		attributes: [
			{ name: 'mid', description: 'Media ID for the video', type: 'integer', required: true },
			{ name: 'wid', description: 'Widget ID for custom settings', type: 'integer' },
			{ name: 'url', description: 'Direct URL to video (alternative to mid)', type: 'url' },
			{ name: 'width', description: 'Player width in pixels', type: 'integer', default: '480' },
			{ name: 'height', description: 'Player height in pixels', type: 'integer', default: '360' },
			{ name: 'autoPlay', description: 'Auto-play on load', type: 'boolean' },
			{ name: 'class', description: 'HTML class attribute', type: 'string' },
			{ name: 'id', description: 'HTML id attribute', type: 'string' },
			{ name: 'style', description: 'Inline CSS styles', type: 'string' },
			{ name: 'externalMode', description: 'Generate HTML for external embedding', type: 'boolean' },
		],
		selfClosing: true,
		snippet: '<fm:VideoPlayer mid="" width="640" height="360" />',
	},
	{
		name: 'MediaViewer',
		description: 'Full media viewer with controls',
		category: cat.media,
		attributes: [
			{ name: 'mid', description: 'Media ID', type: 'integer', required: true },
			{ name: 'wid', description: 'Widget ID', type: 'integer' },
		],
		selfClosing: true,
		snippet: '<fm:MediaViewer mid="" />',
	},
	{
		name: 'Gallery',
		description: 'Media gallery widget',
		category: cat.widget,
		attributes: [
			{ name: 'wid', description: 'Widget ID', type: 'integer', required: true },
		],
		selfClosing: true,
		snippet: '<fm:Gallery wid="" />',
	},
	{
		name: 'Slideshow',
		description: 'Image slideshow widget',
		category: cat.widget,
		attributes: [
			{ name: 'wid', description: 'Widget ID', type: 'integer', required: true },
		],
		selfClosing: true,
	},
	{
		name: 'Slideshow2',
		description: 'Advanced slideshow widget v2',
		category: cat.widget,
		attributes: [
			{ name: 'wid', description: 'Widget ID', type: 'integer', required: true },
		],
		selfClosing: true,
	},
	{
		name: 'VideoWall',
		description: 'Grid of video thumbnails',
		category: cat.widget,
		attributes: [
			{ name: 'wid', description: 'Widget ID', type: 'integer', required: true },
		],
		selfClosing: true,
	},
	{
		name: 'MediaUrl',
		description: 'Output a media URL for a given media item',
		category: cat.media,
		selfClosing: true,
		attributes: [
			{ name: 'mid', description: 'Media ID', type: 'integer', required: true },
			{ name: 'type', description: 'URL type (thumb, public, original)', type: 'string' },
		],
	},
	{
		name: 'MediaMetrics',
		description: 'Display media metrics (views, votes, comments)',
		category: cat.media,
		attributes: [
			{ name: 'mid', description: 'Media ID', type: 'integer', required: true },
		],
		variables: [
			{ name: '$metrics.views', description: 'View count' },
			{ name: '$metrics.votes', description: 'Vote count' },
			{ name: '$metrics.comments', description: 'Comment count' },
		],
	},
	{
		name: 'MediaExternalIds',
		description: 'List external platform IDs for a media item',
		category: cat.media,
		attributes: [
			{ name: 'mid', description: 'Media ID', type: 'integer', required: true },
		],
	},
	{
		name: 'MediaNext',
		description: 'Link/info for the next media item in sequence',
		category: cat.media,
		attributes: [
			{ name: 'mid', description: 'Current media ID', type: 'integer', required: true },
		],
	},
	{
		name: 'MediaPrevious',
		description: 'Link/info for the previous media item in sequence',
		category: cat.media,
		attributes: [
			{ name: 'mid', description: 'Current media ID', type: 'integer', required: true },
		],
	},
	{
		name: 'MediaNextPrevious',
		description: 'Next/previous navigation for media',
		category: cat.media,
		attributes: [
			{ name: 'mid', description: 'Current media ID', type: 'integer', required: true },
		],
	},
	{
		name: 'CommentList',
		description: 'List comments for a media item',
		category: cat.media,
		attributes: [
			{ name: 'mid', description: 'Media ID', type: 'integer', required: true },
			{ name: 'pageSize', description: 'Comments per page', type: 'integer' },
			{ name: 'sort', description: 'Sort order', type: 'string' },
		],
	},
	{
		name: 'CollectionList',
		description: 'List collections for the current application',
		category: cat.media,
		attributes: [
			{ name: 'uid', description: 'Filter by user ID', type: 'integer' },
			{ name: 'pageSize', description: 'Items per page', type: 'integer' },
		],
	},
	{
		name: 'CollectionInfo',
		description: 'Display info about a specific collection',
		category: cat.media,
		attributes: [
			{ name: 'collectionId', description: 'Collection ID', type: 'integer', required: true },
		],
	},
	{
		name: 'MediaCollectionList',
		description: 'List media items within a collection',
		category: cat.media,
		attributes: [
			{ name: 'collectionId', description: 'Collection ID', type: 'integer', required: true },
			{ name: 'pageSize', description: 'Items per page', type: 'integer' },
		],
	},
	{
		name: 'TagCloud',
		description: 'Display a tag cloud for media items',
		category: cat.media,
		attributes: [
			{ name: 'limit', description: 'Maximum number of tags', type: 'integer' },
		],
		selfClosing: true,
	},
	{
		name: 'RandomVideo',
		description: 'Display a random video',
		category: cat.media,
		selfClosing: true,
		attributes: [],
	},
	{
		name: 'Rss',
		description: 'Generate or embed an RSS feed',
		category: cat.media,
		selfClosing: true,
		attributes: [
			{ name: 'channel', description: 'Channel shortname', type: 'string' },
		],
	},

	// ═══ USERS ═══
	{
		name: 'UserInfo',
		description: 'Display information about a user',
		category: cat.user,
		attributes: [
			{ name: 'uid', description: 'User ID (default: current logged-in user)', type: 'integer' },
		],
		variables: [
			{ name: '$user.id', description: 'User ID' },
			{ name: '$user.user', description: 'Username' },
			{ name: '$user.email', description: 'Email address' },
			{ name: '$user.firstname', description: 'First name' },
			{ name: '$user.lastname', description: 'Last name' },
			{ name: '$user.phone', description: 'Phone number' },
			{ name: '$user.city', description: 'City' },
			{ name: '$user.country', description: 'Country' },
			{ name: '$user.created', description: 'Account creation date' },
			{ name: '$user.friends', description: 'Friend count' },
			{ name: '$user_metadata', description: 'User metadata array' },
			{ name: '$user.json_meta', description: 'JSON-encoded metadata' },
		],
		snippet: '<fm:UserInfo uid="">\n\t{$$user.firstname} {$$user.lastname}\n</fm:UserInfo>',
	},
	{
		name: 'UserList',
		description: 'Loop through a list of users',
		category: cat.user,
		attributes: [
			{ name: 'pageSize', description: 'Users per page', type: 'integer' },
			{ name: 'sort', description: 'Sort order', type: 'string' },
			{ name: 'groupId', description: 'Filter by group', type: 'integer' },
		],
	},
	{
		name: 'UserAvatar',
		description: 'Display user avatar image',
		category: cat.user,
		selfClosing: true,
		attributes: [
			{ name: 'uid', description: 'User ID', type: 'integer' },
			{ name: 'size', description: 'Avatar size in pixels', type: 'integer' },
		],
	},
	{
		name: 'UserRank',
		description: 'Display user rank/leaderboard position',
		category: cat.user,
		attributes: [
			{ name: 'uid', description: 'User ID', type: 'integer' },
		],
	},
	{
		name: 'UserNext',
		description: 'Link to next user in sequence',
		category: cat.user,
		attributes: [
			{ name: 'uid', description: 'Current user ID', type: 'integer' },
		],
	},
	{
		name: 'UserPrevious',
		description: 'Link to previous user in sequence',
		category: cat.user,
		attributes: [
			{ name: 'uid', description: 'Current user ID', type: 'integer' },
		],
	},
	{
		name: 'Friends',
		description: 'List friends for a user',
		category: cat.social,
		attributes: [
			{ name: 'uid', description: 'User ID', type: 'integer' },
			{ name: 'pageSize', description: 'Friends per page', type: 'integer' },
		],
	},
	{
		name: 'FriendStatus',
		description: 'Check friendship status between users',
		category: cat.social,
		attributes: [
			{ name: 'uid', description: 'Target user ID', type: 'integer', required: true },
		],
	},
	{
		name: 'LeaderBoard',
		description: 'Display a leaderboard',
		category: cat.social,
		attributes: [
			{ name: 'limit', description: 'Number of entries', type: 'integer' },
			{ name: 'type', description: 'Leaderboard type', type: 'string' },
		],
	},

	// ═══ LAYOUT & LOGIC ═══
	{
		name: 'If',
		description: 'Conditional rendering — supports truthy, equals, contains, between, regex, etc.',
		category: cat.layout,
		attributes: [
			{ name: 'value', description: 'The variable or value to test', type: 'string', required: true },
			{ name: 'comparison', description: 'Comparison type', type: 'string', default: 'truthy', values: ['truthy', '=', 'integer', 'gt', 'gte', 'lt', 'lte', 'contains', 'inList', 'empty', 'notempty', 'ne', 'matches', 'between', 'exists'] },
			{ name: 'compareTo', description: 'Value to compare against', type: 'string' },
			{ name: 'delimiter', description: 'Delimiter for inList comparison', type: 'string', default: ',' },
			{ name: 'start', description: 'Range start (for between)', type: 'string' },
			{ name: 'end', description: 'Range end (for between)', type: 'string' },
			{ name: 'key', description: 'Key to check (for exists)', type: 'string' },
		],
		snippet: '<fm:If value="{$$variable}" comparison="truthy">\n\t<!-- content -->\n</fm:If>',
	},
	{
		name: 'IfLoggedIn',
		description: 'Show content only to logged-in users',
		category: cat.layout,
		attributes: [],
		snippet: '<fm:IfLoggedIn>\n\t<!-- logged-in content -->\n</fm:IfLoggedIn>',
	},
	{
		name: 'IfImage',
		description: 'Show content if media is an image',
		category: cat.layout,
		attributes: [],
	},
	{
		name: 'IfVideo',
		description: 'Show content if media is a video',
		category: cat.layout,
		attributes: [],
	},
	{
		name: 'IfAudio',
		description: 'Show content if media is audio',
		category: cat.layout,
		attributes: [],
	},
	{
		name: 'IfText',
		description: 'Show content if media is text/document',
		category: cat.layout,
		attributes: [],
	},
	{
		name: 'IfIsUser',
		description: 'Show content if viewing user\'s own content',
		category: cat.layout,
		attributes: [],
	},
	{
		name: 'IfCanAccess',
		description: 'Show content if user has permission to access',
		category: cat.layout,
		attributes: [
			{ name: 'permission', description: 'Permission name to check', type: 'string' },
		],
	},
	{
		name: 'IfCanDisplay',
		description: 'Conditional display based on display rules',
		category: cat.layout,
		attributes: [],
	},
	{
		name: 'IfInGroup',
		description: 'Show content if media is in a specific group',
		category: cat.layout,
		attributes: [
			{ name: 'groupId', description: 'Group ID to check', type: 'integer', required: true },
		],
	},
	{
		name: 'IfUserHasUploaded',
		description: 'Show content if user has uploaded media',
		category: cat.layout,
		attributes: [],
	},
	{
		name: 'IfUserHasVoted',
		description: 'Show content if user has voted',
		category: cat.layout,
		attributes: [],
	},
	{
		name: 'IfCookie',
		description: 'Show content based on cookie value',
		category: cat.layout,
		attributes: [
			{ name: 'name', description: 'Cookie name', type: 'string', required: true },
			{ name: 'value', description: 'Expected cookie value', type: 'string' },
		],
	},
	{
		name: 'IfSSL',
		description: 'Show content only over HTTPS',
		category: cat.layout,
		attributes: [],
	},
	{
		name: 'IfDomain',
		description: 'Show content only on specific domain',
		category: cat.layout,
		attributes: [
			{ name: 'domain', description: 'Domain to match', type: 'string', required: true },
		],
	},
	{
		name: 'IfGeoblocked',
		description: 'Show content based on geo-blocking rules',
		category: cat.layout,
		attributes: [],
	},
	{
		name: 'IfAfter',
		description: 'Show content after a specific date/time',
		category: cat.layout,
		attributes: [
			{ name: 'date', description: 'Date/time threshold', type: 'string', required: true },
		],
	},
	{
		name: 'IfBetween',
		description: 'Show content between two dates',
		category: cat.layout,
		attributes: [
			{ name: 'start', description: 'Start date', type: 'string', required: true },
			{ name: 'end', description: 'End date', type: 'string', required: true },
		],
	},
	{
		name: 'For',
		description: 'Loop a fixed number of times',
		category: cat.layout,
		attributes: [
			{ name: 'size', description: 'Number of iterations', type: 'integer', required: true },
		],
		variables: [
			{ name: '$list', description: 'Current iteration item' },
		],
		snippet: '<fm:For size="10">\n\t{$$list}\n</fm:For>',
	},
	{
		name: 'Switch',
		description: 'Switch/case conditional rendering',
		category: cat.layout,
		attributes: [
			{ name: 'value', description: 'Value to switch on', type: 'string', required: true },
		],
	},
	{
		name: 'Include',
		description: 'Include another template by name',
		category: cat.layout,
		attributes: [
			{ name: 'name', description: 'Template name to include', type: 'string', required: true },
			{ name: 'includeContext', description: 'Loading context (default: app templates)', type: 'string' },
			{ name: 'type', description: 'File type to include (tpl, js, css)', type: 'string', default: 'tpl', values: ['tpl', 'js', 'css'] },
			{ name: 'processText', description: 'Text processing option', type: 'string', values: ['jsminify', 'cssminify'] },
		],
		selfClosing: true,
		snippet: '<fm:Include name="" />',
	},

	// ═══ NAVIGATION ═══
	{
		name: 'Pager',
		description: 'Pagination component with customizable templates',
		category: cat.nav,
		selfClosing: true,
		attributes: [
			{ name: 'currentPage', description: 'Current page number', type: 'integer', required: true },
			{ name: 'totalPages', description: 'Total number of pages', type: 'integer' },
			{ name: 'requestVars', description: 'Comma-separated request vars to preserve', type: 'string' },
			{ name: 'linkTemplate', description: 'HTML template for page links (%page, %pageplus1 tokens)', type: 'string' },
			{ name: 'selectedTemplate', description: 'Template for current page', type: 'string' },
			{ name: 'previousTemplate', description: 'Template for previous button', type: 'string' },
			{ name: 'nextTemplate', description: 'Template for next button', type: 'string' },
			{ name: 'separatorTemplate', description: 'Separator between page ranges', type: 'string', default: '...' },
			{ name: 'numPages', description: 'Max pages to display', type: 'integer' },
		],
		snippet: '<fm:Pager currentPage="{$$pager.currentPage}" totalPages="{$$pager.totalPages}" />',
	},
	{
		name: 'NavBarList',
		description: 'Navigation bar / menu list',
		category: cat.nav,
		attributes: [],
	},

	// ═══ FORMS ═══
	{
		name: 'Form',
		description: 'HTML form wrapper with validation support',
		category: cat.form,
		attributes: [
			{ name: 'name', description: 'Form name', type: 'string' },
			{ name: 'action', description: 'Form action URL', type: 'url' },
			{ name: 'method', description: 'HTTP method', type: 'string', default: 'POST', values: ['GET', 'POST'] },
			{ name: 'enctype', description: 'Encoding type', type: 'string' },
			{ name: 'class', description: 'CSS class', type: 'string' },
			{ name: 'onsubmit', description: 'JavaScript submit handler', type: 'string' },
		],
		snippet: '<fm:Form action="" method="POST">\n\t\n</fm:Form>',
	},
	{
		name: 'Form:TextInput',
		description: 'Text input field',
		category: cat.form,
		selfClosing: true,
		attributes: [
			{ name: 'name', description: 'Field name', type: 'string', required: true },
			{ name: 'value', description: 'Field value', type: 'string' },
			{ name: 'label', description: 'Field label', type: 'string' },
			{ name: 'id', description: 'Element ID', type: 'string' },
			{ name: 'class', description: 'CSS classes', type: 'string' },
			{ name: 'maxlength', description: 'Maximum character length', type: 'integer' },
			{ name: 'placeholder', description: 'Placeholder text', type: 'string' },
			{ name: 'required', description: 'Mark as required', type: 'boolean' },
			{ name: 'readonly', description: 'Make read-only', type: 'boolean' },
			{ name: 'disabled', description: 'Disable field', type: 'boolean' },
		],
		snippet: '<fm:Form:TextInput name="" label="" />',
	},
	{
		name: 'Form:TextArea',
		description: 'Textarea field',
		category: cat.form,
		selfClosing: true,
		attributes: [
			{ name: 'name', description: 'Field name', type: 'string', required: true },
			{ name: 'value', description: 'Field value', type: 'string' },
			{ name: 'label', description: 'Field label', type: 'string' },
			{ name: 'rows', description: 'Number of visible rows', type: 'integer' },
			{ name: 'cols', description: 'Number of visible columns', type: 'integer' },
			{ name: 'class', description: 'CSS classes', type: 'string' },
			{ name: 'required', description: 'Mark as required', type: 'boolean' },
		],
		snippet: '<fm:Form:TextArea name="" label="" rows="4" />',
	},
	{
		name: 'Form:Select',
		description: 'Dropdown select field',
		category: cat.form,
		attributes: [
			{ name: 'name', description: 'Field name', type: 'string', required: true },
			{ name: 'value', description: 'Selected value', type: 'string' },
			{ name: 'selectValue', description: 'Alternative selected value attribute', type: 'string' },
			{ name: 'label', description: 'Field label', type: 'string' },
			{ name: 'id', description: 'Element ID', type: 'string' },
			{ name: 'class', description: 'CSS classes', type: 'string' },
			{ name: 'multiple', description: 'Allow multiple selection', type: 'boolean' },
			{ name: 'size', description: 'Number of visible options', type: 'integer' },
			{ name: 'required', description: 'Mark as required', type: 'boolean' },
		],
		snippet: '<fm:Form:Select name="" label="">\n\t<fm:Form:Option value="">Label</fm:Form:Option>\n</fm:Form:Select>',
	},
	{
		name: 'Form:Option',
		description: 'Option within a select field',
		category: cat.form,
		attributes: [
			{ name: 'value', description: 'Option value', type: 'string', required: true },
		],
	},
	{
		name: 'Form:CheckBox',
		description: 'Checkbox input field',
		category: cat.form,
		selfClosing: true,
		attributes: [
			{ name: 'name', description: 'Field name', type: 'string', required: true },
			{ name: 'value', description: 'Checked value', type: 'string' },
			{ name: 'label', description: 'Field label', type: 'string' },
			{ name: 'checked', description: 'Default checked state', type: 'boolean' },
		],
		snippet: '<fm:Form:CheckBox name="" label="" value="1" />',
	},
	{
		name: 'Form:Password',
		description: 'Password input field',
		category: cat.form,
		selfClosing: true,
		attributes: [
			{ name: 'name', description: 'Field name', type: 'string', required: true },
			{ name: 'label', description: 'Field label', type: 'string' },
			{ name: 'required', description: 'Mark as required', type: 'boolean' },
		],
	},
	{
		name: 'Form:Email',
		description: 'Email input field',
		category: cat.form,
		selfClosing: true,
		attributes: [
			{ name: 'name', description: 'Field name', type: 'string', required: true },
			{ name: 'label', description: 'Field label', type: 'string' },
			{ name: 'required', description: 'Mark as required', type: 'boolean' },
		],
	},
	{
		name: 'Form:FileInput',
		description: 'File upload input',
		category: cat.form,
		selfClosing: true,
		attributes: [
			{ name: 'name', description: 'Field name', type: 'string', required: true },
			{ name: 'accept', description: 'Accepted file types', type: 'string' },
		],
	},
	{
		name: 'Form:Date',
		description: 'Date picker input',
		category: cat.form,
		selfClosing: true,
		attributes: [
			{ name: 'name', description: 'Field name', type: 'string', required: true },
			{ name: 'value', description: 'Default date', type: 'string' },
			{ name: 'label', description: 'Field label', type: 'string' },
		],
	},
	{
		name: 'Form:Country',
		description: 'Country dropdown select',
		category: cat.form,
		selfClosing: true,
		attributes: [
			{ name: 'name', description: 'Field name', type: 'string', required: true },
			{ name: 'value', description: 'Selected country code', type: 'string' },
		],
	},
	{
		name: 'Form:State',
		description: 'State/province dropdown',
		category: cat.form,
		selfClosing: true,
		attributes: [
			{ name: 'name', description: 'Field name', type: 'string', required: true },
			{ name: 'country', description: 'Filter by country', type: 'string' },
		],
	},
	{
		name: 'Form:GroupTree',
		description: 'Hierarchical group selector',
		category: cat.form,
		selfClosing: true,
		attributes: [
			{ name: 'name', description: 'Field name', type: 'string', required: true },
		],
	},
	{
		name: 'Form:ChannelTree',
		description: 'Hierarchical channel selector',
		category: cat.form,
		selfClosing: true,
		attributes: [
			{ name: 'name', description: 'Field name', type: 'string', required: true },
		],
	},
	{
		name: 'Form:ColorPicker',
		description: 'Color picker input',
		category: cat.form,
		selfClosing: true,
		attributes: [
			{ name: 'name', description: 'Field name', type: 'string', required: true },
			{ name: 'value', description: 'Default color', type: 'string' },
		],
	},
	{
		name: 'Captcha',
		description: 'CAPTCHA verification',
		category: cat.form,
		selfClosing: true,
		attributes: [],
	},
	{
		name: 'UploadForm',
		description: 'File upload form with progress',
		category: cat.form,
		attributes: [
			{ name: 'fileTypes', description: 'Allowed file types (1=image, 2=video, 3=audio)', type: 'string' },
			{ name: 'maxSize', description: 'Max file size in KB', type: 'integer' },
			{ name: 'redirectUrl', description: 'Redirect URL after upload', type: 'url' },
			{ name: 'fileLimit', description: 'Max number of files', type: 'integer' },
			{ name: 'onUploadStart', description: 'JS callback on upload start', type: 'string' },
			{ name: 'onUploadError', description: 'JS callback on error', type: 'string' },
		],
		snippet: '<fm:UploadForm fileTypes="1,2" maxSize="50000">\n\t\n</fm:UploadForm>',
	},
	{
		name: 'Uploader',
		description: 'Advanced media uploader widget',
		category: cat.form,
		selfClosing: true,
		attributes: [
			{ name: 'wid', description: 'Widget ID', type: 'integer' },
		],
	},

	// ═══ GROUPS / CHANNELS / EVENTS ═══
	{
		name: 'GroupList',
		description: 'List groups',
		category: cat.nav,
		attributes: [
			{ name: 'pageSize', description: 'Groups per page', type: 'integer' },
		],
	},
	{
		name: 'GroupTree',
		description: 'Hierarchical group tree',
		category: cat.nav,
		attributes: [],
	},
	{
		name: 'GroupInfo',
		description: 'Display group information',
		category: cat.nav,
		attributes: [
			{ name: 'groupId', description: 'Group ID', type: 'integer', required: true },
		],
	},
	{
		name: 'ChannelList',
		description: 'List channels',
		category: cat.nav,
		attributes: [
			{ name: 'pageSize', description: 'Channels per page', type: 'integer' },
		],
	},
	{
		name: 'ChannelInfo',
		description: 'Display channel information',
		category: cat.nav,
		attributes: [
			{ name: 'channelId', description: 'Channel ID', type: 'integer', required: true },
		],
	},
	{
		name: 'EventList',
		description: 'List events',
		category: cat.nav,
		attributes: [
			{ name: 'pageSize', description: 'Events per page', type: 'integer' },
		],
	},
	{
		name: 'EventInfo',
		description: 'Display event information',
		category: cat.nav,
		attributes: [
			{ name: 'eventId', description: 'Event ID', type: 'integer', required: true },
		],
	},

	// ═══ UTILITY ═══
	{
		name: 'Var',
		description: 'Set or output a template variable',
		category: cat.util,
		selfClosing: true,
		attributes: [
			{ name: 'name', description: 'Variable name', type: 'string', required: true },
			{ name: 'value', description: 'Variable value', type: 'string' },
		],
		snippet: '<fm:Var name="" value="" />',
	},
	{
		name: 'DateFormat',
		description: 'Format a date string',
		category: cat.util,
		selfClosing: true,
		attributes: [
			{ name: 'date', description: 'Date to format', type: 'string', required: true },
			{ name: 'format', description: 'Output format (PHP date())', type: 'string', default: 'Y-m-d' },
		],
	},
	{
		name: 'Truncate',
		description: 'Truncate text to a maximum length',
		category: cat.util,
		selfClosing: true,
		attributes: [
			{ name: 'text', description: 'Text to truncate', type: 'string', required: true },
			{ name: 'length', description: 'Max character length', type: 'integer', required: true },
			{ name: 'suffix', description: 'Truncation suffix', type: 'string', default: '...' },
		],
	},
	{
		name: 'TextFunctions',
		description: 'String manipulation utilities',
		category: cat.util,
		selfClosing: true,
		attributes: [
			{ name: 'text', description: 'Input text', type: 'string', required: true },
			{ name: 'function', description: 'Function to apply', type: 'string', values: ['uppercase', 'lowercase', 'ucfirst', 'trim', 'urlencode', 'htmlencode'] },
		],
	},
	{
		name: 'Math',
		description: 'Mathematical operations',
		category: cat.util,
		selfClosing: true,
		attributes: [
			{ name: 'expression', description: 'Math expression to evaluate', type: 'string', required: true },
		],
	},
	{
		name: 'DataToJson',
		description: 'Convert template data to JSON',
		category: cat.util,
		selfClosing: true,
		attributes: [
			{ name: 'data', description: 'Data variable to convert', type: 'string', required: true },
		],
	},
	{
		name: 'JsonToObject',
		description: 'Parse JSON string into template object',
		category: cat.util,
		attributes: [
			{ name: 'json', description: 'JSON string to parse', type: 'string', required: true },
			{ name: 'var', description: 'Variable name to store result', type: 'string', required: true },
		],
	},
	{
		name: 'Cache',
		description: 'Cache a block of template output',
		category: cat.util,
		attributes: [
			{ name: 'key', description: 'Cache key', type: 'string', required: true },
			{ name: 'ttl', description: 'Time-to-live in seconds', type: 'integer', default: '3600' },
		],
	},
	{
		name: 'Encrypt',
		description: 'Encrypt a value',
		category: cat.util,
		selfClosing: true,
		attributes: [
			{ name: 'value', description: 'Value to encrypt', type: 'string', required: true },
		],
	},
	{
		name: 'Decrypt',
		description: 'Decrypt a value',
		category: cat.util,
		selfClosing: true,
		attributes: [
			{ name: 'value', description: 'Value to decrypt', type: 'string', required: true },
		],
	},
	{
		name: 'LogHit',
		description: 'Log a page hit or custom event',
		category: cat.util,
		selfClosing: true,
		attributes: [
			{ name: 'type', description: 'Hit type', type: 'string' },
		],
	},
	{
		name: 'Logout',
		description: 'Logout link/action',
		category: cat.util,
		selfClosing: true,
		attributes: [
			{ name: 'redirectUrl', description: 'URL to redirect after logout', type: 'url' },
		],
	},
	{
		name: 'PageTitle',
		description: 'Set the page title',
		category: cat.util,
		selfClosing: true,
		attributes: [
			{ name: 'title', description: 'Page title text', type: 'string', required: true },
		],
	},
	{
		name: 'Meta',
		description: 'Output an HTML meta tag',
		category: cat.util,
		selfClosing: true,
		attributes: [
			{ name: 'name', description: 'Meta tag name', type: 'string' },
			{ name: 'content', description: 'Meta content', type: 'string' },
			{ name: 'property', description: 'Meta property (Open Graph)', type: 'string' },
		],
	},
	{
		name: 'MetaList',
		description: 'Loop through custom metadata fields',
		category: cat.util,
		attributes: [
			{ name: 'mid', description: 'Media ID', type: 'integer' },
			{ name: 'uid', description: 'User ID', type: 'integer' },
		],
	},
	{
		name: 'JSLibrary',
		description: 'Include a JavaScript library',
		category: cat.util,
		selfClosing: true,
		attributes: [
			{ name: 'name', description: 'Library name', type: 'string', required: true },
		],
	},
	{
		name: 'Copyright',
		description: 'Display copyright notice',
		category: cat.util,
		selfClosing: true,
		attributes: [],
	},
	{
		name: 'PoweredBy',
		description: 'Display "Powered by" branding',
		category: cat.util,
		selfClosing: true,
		attributes: [],
	},
	{
		name: 'Version',
		description: 'Output platform version',
		category: cat.util,
		selfClosing: true,
		attributes: [],
	},
	{
		name: 'GeoInfo',
		description: 'Display geographic information for current visitor',
		category: cat.util,
		attributes: [],
		variables: [
			{ name: '$geo.country', description: 'Visitor country' },
			{ name: '$geo.city', description: 'Visitor city' },
			{ name: '$geo.region', description: 'Visitor region' },
			{ name: '$geo.lat', description: 'Visitor latitude' },
			{ name: '$geo.lng', description: 'Visitor longitude' },
		],
	},
	{
		name: 'RemoteInclude',
		description: 'Include content from a remote URL',
		category: cat.util,
		selfClosing: true,
		attributes: [
			{ name: 'url', description: 'Remote URL to include', type: 'url', required: true },
			{ name: 'cache', description: 'Cache duration in seconds', type: 'integer' },
		],
	},
	{
		name: 'ExternalContent',
		description: 'Embed external content',
		category: cat.util,
		selfClosing: true,
		attributes: [
			{ name: 'url', description: 'Content URL', type: 'url', required: true },
		],
	},
	{
		name: 'Sharelizer',
		description: 'Social sharing buttons',
		category: cat.social,
		selfClosing: true,
		attributes: [
			{ name: 'url', description: 'URL to share', type: 'url' },
			{ name: 'title', description: 'Share title', type: 'string' },
		],
	},
	{
		name: 'Gravatar',
		description: 'Display Gravatar image for an email',
		category: cat.user,
		selfClosing: true,
		attributes: [
			{ name: 'email', description: 'Email address', type: 'string', required: true },
			{ name: 'size', description: 'Image size in pixels', type: 'integer', default: '80' },
		],
	},
	{
		name: 'Achievements',
		description: 'Display user achievements',
		category: cat.social,
		attributes: [
			{ name: 'uid', description: 'User ID', type: 'integer' },
		],
	},
	{
		name: 'Notify',
		description: 'Send a notification',
		category: cat.util,
		selfClosing: true,
		attributes: [
			{ name: 'type', description: 'Notification type', type: 'string' },
			{ name: 'message', description: 'Notification message', type: 'string' },
		],
	},
	{
		name: 'LiveFeed',
		description: 'Real-time content feed',
		category: cat.media,
		attributes: [
			{ name: 'channel', description: 'Channel to subscribe to', type: 'string' },
		],
	},
	{
		name: 'Recorder',
		description: 'Audio/video recorder widget',
		category: cat.media,
		selfClosing: true,
		attributes: [
			{ name: 'type', description: 'Recording type (audio, video)', type: 'string', values: ['audio', 'video'] },
		],
	},
	{
		name: 'HTMLSanitizer',
		description: 'Sanitize HTML content to prevent XSS',
		category: cat.util,
		attributes: [
			{ name: 'allowedTags', description: 'Comma-separated list of allowed HTML tags', type: 'string' },
		],
	},
	{
		name: 'AuditLog',
		description: 'Display audit log entries',
		category: cat.util,
		attributes: [
			{ name: 'limit', description: 'Max entries to show', type: 'integer' },
		],
	},
];

// Build a lookup map for quick access
export const fmComponentMap = new Map<string, FMComponentDef>();
fmComponents.forEach(c => fmComponentMap.set(c.name, c));

// Category list for UI grouping
export const fmCategories = [...new Set(fmComponents.map(c => c.category))].sort();

// Get all component names for quick autocomplete matching
export const fmComponentNames = fmComponents.map(c => c.name);
